import React, { useMemo, useCallback } from 'react';
import { Text, View, Image } from '@react-pdf/renderer';
import { ensureFontsRegistered } from '../../../shared/PdfFontRegistry';
import { unit as unitEnum } from '../../../../../enum/project';
import { convertFieldValue } from '../../../../../services/UnitConversionService';
import { pdfSharedStyles } from '../../../pdfSharedStyles';
import { isEmptyUnit } from '../../shared/pdfUnitMerge';
import * as symbols from '../../../../../enum/symbols';
import { TFunction } from 'i18next';
import { IRailwayImages } from '../../../../../services/PdfImageCaptureService';
import { IProject } from '../../../../../api/IApiResponse';
import { getBallastLabel } from '../../../../../services/BallastCbrDatabase';
import { getSubgradeSoilTypeLabel } from '../../../../../services/ProjectTypeService';

interface IRailwayGeogridData {
    selectedGeogrid?: string;
    tensileStrengthAt2Percent?: number;
}

interface IProjectInputs {
    geoGrid?: IRailwayGeogridData;
    setStabilisedBallastThickness?: number;
    designParameters?: {
        setStabilisedBallastThickness?: number;
    };
    railwayType?: {
        railwayToDesign?: string;
        railwayTypeName?: string;
    };
    trackGeometry?: {
        railWidth?: number;
        sleeperThickness?: number;
        sleeperWidth?: number;
        loadSpreadingAngleInSleepers?: number;
        loadSpreadingAngleInBallast?: number;
        loadSpreadingAngleInSubballast?: number;
        loadSpreadingAngle?: number;
    };
    loadData?: {
        designLoad?: number;
        dynamicFactor?: number;
        // User input fields
        designAxleLoad?: number;
        trainSpeed?: number;
        wheelDiameter?: number;
        // Mixed train cargo fields
        designAxleLoadCargo?: number;
        trainSpeedCargo?: number;
        wheelDiameterCargo?: number;
        // Mixed train passenger fields
        designAxleLoadPassenger?: number;
        trainSpeedPassenger?: number;
        wheelDiameterPassenger?: number;
    };
    trafficData?: {
        annualTonnage?: number;
        numberOfAxles?: number;
        // User input fields
        numberOfTrainsPerDay?: number;
        numberOfCarsPerTrain?: number;
        numberOfAxisPerCar?: number;
        designLife?: number;
        // Mixed train cargo fields
        numberOfTrainsPerDayCargo?: number;
        numberOfCarsPerTrainCargo?: number;
        numberOfAxisPerCarCargo?: number;
        designLifeCargo?: number;
        // Mixed train passenger fields
        numberOfTrainsPerDayPassenger?: number;
        numberOfCarsPerTrainPassenger?: number;
        numberOfAxisPerCarPassenger?: number;
        designLifePassenger?: number;
    };
    soilProperties?: {
        subgradeModulus?: number;
        thicknessOfSoftSubgrade?: number;
        cbrOfBallast?: number;
        cbrOfSubballast?: number;
        cbrOfSubgrade?: number;
    };
    materialTypes?: {
        ballastType?: string;
        subgradeSoilType?: string;
    };
    poissonRatios?: {
        poissonRatioOfBallast?: number;
        poissonRatioOfSubballast?: number;
        poissonRatioOfSubgrade?: number;
    };
    reinforcement?: {
        setBallastThickness?: number;
    };
}

interface IDesignSummaryItem {
    description?: string;
    unstabilizedSymbol?: string;
    unstabilizedValue?: number;
    unstabilizedUnit?: string;
    stabilizedSymbol?: string;
    stabilizedValue?: number;
    stabilizedUnit?: string;
}

interface ITrainAnalysisItem {
    description?: string;
    passengerSymbol?: string;
    passengerValue?: number | string;
    passengerUnit?: string;
    cargoSymbol?: string;
    cargoValue?: number | string;
    cargoUnit?: string;
}

interface ICalculatedDataRow {
    symbol?: string;
    description?: string;
    value?: number | string;
    unit?: string;
}


// Funzione di parsing base per <br> e <strong>
function parseHtmlToPdfText(html: string, style?: any) {
    // Sostituisce <br> con \n
    const withBreaks = html.replace(/<br\s*\/??\s*>/gi, '\n');
    // Regex per trovare <strong>...</strong>
    const regex = /(<strong>(.*?)<\/strong>)/gi;
    const result: JSX.Element[] = [];
    let lastIndex = 0;
    let match;
    let key = 0;

    while ((match = regex.exec(withBreaks)) !== null) {
        // Testo prima del tag
        if (match.index > lastIndex) {
            const text = withBreaks.substring(lastIndex, match.index);
            if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
        }
        // Testo in grassetto
        const boldText = match[2];
        if (boldText) {
            result.push(
                <Text
                    key={key++}
                    style={{
                        ...style,
                        fontFamily: 'DejaVuSans',
                        fontWeight: 'bold',
                    }}
                >
                    {boldText}
                </Text>
            );
        }
        lastIndex = regex.lastIndex;
    }
    // Testo dopo l'ultimo tag
    if (lastIndex < withBreaks.length) {
        const text = withBreaks.substring(lastIndex);
        if (text) result.push(<Text key={key++} style={style}>{text}</Text>);
    }
    return result;
}

interface RailwayResultsCoreProps {
    railwayImages: IRailwayImages;
    projectData: IProject;
    output: {
        designSummary?: IDesignSummaryItem[];
        trainAnalysis?: ITrainAnalysisItem[];
        calculatedData?: {
            rows: ICalculatedDataRow[];
        };
    };
    t: TFunction;
    showInputData?: boolean;
    showResults?: boolean;
    customTitle?: string;
}

export const RailwayResultsCore: React.FC<RailwayResultsCoreProps> = ({ railwayImages, projectData, output, t, showInputData = true, showResults = true, customTitle }) => {
        // Assicura che i font siano registrati
        ensureFontsRegistered();
    const unitSystem = projectData?.unit || 'Metric';
    // 🔧 FIX: Railway now uses FLAT structure (no .default wrapper)
    // REASON: Unified with other projects after transformProjectFromBackend()
    const projectInputs = projectData?.projectInfo?.data as unknown as IProjectInputs;

    // Get railway type selection to determine which tables to show
    const railwayToDesign = projectInputs?.railwayType?.railwayToDesign;

    // Get geogrid data
    const geoGridData = projectInputs?.geoGrid;
    const selectedGeogrid = geoGridData?.selectedGeogrid;
    const tensileStrengthAt2Percent = geoGridData?.tensileStrengthAt2Percent;

    // Get backend response data for dynamic tables (designSummary and trainAnalysis)
    const backendResponseData = output;

    // Determine if has subballast based on setStabilisedBallastThickness
    const hasSubballast = useMemo(() => {
        // Try multiple possible locations for this value
        const fromFormFields = projectData?.formFields?.setStabilisedBallastThickness as number;
        const fromProjectData = projectInputs?.setStabilisedBallastThickness;
        const fromDesignParameters = projectInputs?.designParameters?.setStabilisedBallastThickness;
        // 🔧 REMOVED: fromDefaultObject - Railway no longer uses .default wrapper

        // Return the first defined value found (including 0), prioritizing form fields
        let value: number;
        if (fromFormFields !== undefined && fromFormFields !== null) {
            value = fromFormFields;
        } else if (fromProjectData !== undefined && fromProjectData !== null) {
            value = fromProjectData;
        } else if (fromDesignParameters !== undefined && fromDesignParameters !== null) {
            value = fromDesignParameters;
        } else {
            // Only use default value if no explicit value is set anywhere
            value = 0.2; // Default value from ProjectTypeService
        }

        return value > 0;
    }, [projectData, projectInputs]);

    // Helper to get units
    const getUnit = useCallback(
        (metricUnit: string, imperialUnit: string) => {
            return unitSystem === 'Metric' ? metricUnit : imperialUnit;
        },
        [unitSystem]
    );

    // Helper for consistent conversion and rounding for input tables
    // FIX: Always convert from Metric to Imperial when in Imperial mode
    // Data in projectData.projectInfo.data is ALWAYS stored in Metric from the backend
    // The conversionApplied flag tracks form display state, NOT the data storage format
    const convertInputValue = useCallback(
        (value: number | undefined, fieldName: string, metricUnit: string, imperialUnit: string): string => {
            if (typeof value !== 'number') return 'N/A';

            // Always convert when Imperial mode - backend data is always Metric
            if (unitSystem === unitEnum.Imperial) {
                const converted = convertFieldValue(
                    { name: fieldName, type: 'number', unit: [metricUnit, imperialUnit], isConvertible: true },
                    value,
                    unitEnum.SI,
                    unitEnum.Imperial,
                    `pdf.${fieldName}`
                );
                return converted !== null ? converted.toFixed(3) : value.toString();
            }

            // Metric mode - no conversion needed
            return value.toFixed(3);
        },
        [unitSystem]
    );

    // Helper for converting output/result values from backend (always metric) to Imperial
    const convertOutputValue = useCallback(
        (value: number | undefined, metricUnit: string, decimals = 3): { value: string; unit: string } => {
            if (value === undefined || value === null) return { value: 'N/A', unit: metricUnit };

            const isImperial = unitSystem === unitEnum.Imperial;
            if (!isImperial) {
                return { value: value.toFixed(decimals), unit: metricUnit };
            }

            // Conversion factors and unit mappings for Imperial
            const conversions: Record<string, { factor: number; imperialUnit: string }> = {
                m: { factor: 3.28084, imperialUnit: 'ft' },
                kN: { factor: 224.809, imperialUnit: 'lbf' },
                kPa: { factor: 0.145038, imperialUnit: 'psi' },
                mm: { factor: 0.0393701, imperialUnit: 'in' },
            };

            const conversion = conversions[metricUnit];
            if (conversion) {
                return {
                    value: (value * conversion.factor).toFixed(decimals),
                    unit: conversion.imperialUnit,
                };
            }

            // No conversion needed for this unit
            return { value: value.toFixed(decimals), unit: metricUnit };
        },
        [unitSystem]
    );

    // Prepare Railway Type Name
    const railwayTypeName = useMemo(() => {
        const railwayType = projectInputs?.railwayType?.railwayToDesign;
        if (!railwayType) return '';
        const typeNames: Record<string, string> = {
            cargo_train: 'Cargo Train',
            passenger_train: 'Passenger Train',
            mixed_train: 'Mixed Train (Cargo + Passenger)',
        };
        return typeNames[railwayType] || railwayType;
    }, [projectInputs]);


    // Prepare Calculated Geometry Values
    const { equivalentLength, widthLoadedArea } = useMemo(() => {
        let equivalentLength: number | undefined;
        let widthLoadedArea: number | undefined;

        if (backendResponseData?.calculatedData?.rows) {
            const equivalentLengthRow = backendResponseData.calculatedData.rows.find(row => row.symbol === 'L' || row.description?.includes('Equivalent length'));
            const widthLoadedAreaRow = backendResponseData.calculatedData.rows.find(row => row.symbol === 'bL' || row.description?.includes('Width of loaded area'));

            if (equivalentLengthRow?.value !== undefined) {
                equivalentLength = typeof equivalentLengthRow.value === 'number' ? equivalentLengthRow.value : parseFloat(String(equivalentLengthRow.value));
            }
            if (widthLoadedAreaRow?.value !== undefined) {
                widthLoadedArea = typeof widthLoadedAreaRow.value === 'number' ? widthLoadedAreaRow.value : parseFloat(String(widthLoadedAreaRow.value));
            }
        }
        return { equivalentLength, widthLoadedArea };
    }, [backendResponseData]);

    // Calculate reinforcement thicknesses - convert to Imperial if needed
    const setBallastThicknessData = useMemo(() => {
        if (backendResponseData?.designSummary && backendResponseData.designSummary.length >= 3) {
            const ballastWithSubballast = backendResponseData.designSummary[2];
            if (ballastWithSubballast?.stabilizedValue !== undefined && ballastWithSubballast?.stabilizedValue !== null) {
                const isImperial = unitSystem === unitEnum.Imperial;
                const value = ballastWithSubballast.stabilizedValue;
                if (isImperial) {
                    // Convert m to ft
                    return { value: (value * 3.28084).toFixed(3), unit: 'ft' };
                }
                return { value: value.toFixed(3), unit: 'm' };
            }
        }
        return { value: 'N/A', unit: 'm' };
    }, [backendResponseData, unitSystem]);

    // === DYNAMIC RESULT TABLES (6 tables) ===


    // 1. Unstabilised without subballast
    const renderUnstabilisedWithoutSubballast = () => {
        if (!backendResponseData?.designSummary || backendResponseData.designSummary.length < 6) return null;

        const ballastThickness = backendResponseData.designSummary[0];
        const deformation = backendResponseData.designSummary[1];
        const rutDepth = backendResponseData.designSummary[5];

        // Convert output values to Imperial if needed
        const ballastConverted = convertOutputValue(ballastThickness.unstabilizedValue, ballastThickness.unstabilizedUnit || 'm');
        const deformationConverted = convertOutputValue(deformation.unstabilizedValue, deformation.unstabilizedUnit || 'mm');
        const rutDepthConverted = convertOutputValue(rutDepth.unstabilizedValue, rutDepth.unstabilizedUnit || 'mm');

        return (
            <View style={{ width: '48%', marginRight: '2%' }}>
                <PdfTable title="UNSTABILISED - without subballast" t={t}>
                    <PdfRow
                        label={ballastThickness.description || 'N/A'}
                        symbol={symbols.sym_hu || 'N/A'}
                        value={ballastConverted.value}
                        unit={ballastConverted.unit}
                    />
                    <PdfRow
                        label={deformation.description ? `${deformation.description} without subballast` : 'N/A'}
                        symbol={symbols.sym_dvu || 'N/A'}
                        value={deformationConverted.value}
                        unit={deformationConverted.unit}
                    />
                    <PdfRow
                        label={rutDepth.description || 'N/A'}
                        symbol={symbols.sym_ru || 'N/A'}
                        value={rutDepthConverted.value}
                        unit={rutDepthConverted.unit}
                        colSpan={1}
                    />
                </PdfTable>
            </View>
        );
    }; // 2. Stabilised without subballast
    const renderStabilisedWithoutSubballast = () => {
        if (!backendResponseData?.designSummary || backendResponseData.designSummary.length < 6) return null;

        const ballastThickness = backendResponseData.designSummary[0];
        const deformation = backendResponseData.designSummary[1];
        const rutDepth = backendResponseData.designSummary[5];

        // Convert output values to Imperial if needed
        const ballastConverted = convertOutputValue(ballastThickness.stabilizedValue, ballastThickness.stabilizedUnit || 'm');
        const deformationConverted = convertOutputValue(deformation.stabilizedValue, deformation.stabilizedUnit || 'mm');
        const rutDepthConverted = convertOutputValue(rutDepth.stabilizedValue, rutDepth.stabilizedUnit || 'mm');

        return (
            <View style={{ width: '48%', marginRight: '2%' }}>
                <PdfTable title="STABILISED - without subballast" t={t}>
                    <PdfRow
                        label={ballastThickness.description || 'N/A'}
                        symbol={symbols.sym_hs || 'N/A'}
                        value={ballastConverted.value}
                        unit={ballastConverted.unit}
                    />
                    <PdfRow
                        label={deformation.description ? `${deformation.description} without subballast` : 'N/A'}
                        symbol={symbols.sym_dvs || 'N/A'}
                        value={deformationConverted.value}
                        unit={deformationConverted.unit}
                    />
                    <PdfRow
                        label={rutDepth.description || 'N/A'}
                        symbol={symbols.sym_rs || 'N/A'}
                        value={rutDepthConverted.value}
                        unit={rutDepthConverted.unit}
                        colSpan={1}
                    />
                </PdfTable>
            </View>
        );
    };

    // 3. Not stabilised with subballast
    const renderNotStabilisedWithSubballast = () => {
        if (!backendResponseData?.designSummary || backendResponseData.designSummary.length < 6) return null;

        const ballastThicknessWithSub = backendResponseData.designSummary[2];
        const subballastThickness = backendResponseData.designSummary[3];
        const deformationWithSub = backendResponseData.designSummary[4];
        const rutDepth = backendResponseData.designSummary[5];

        // Convert output values to Imperial if needed
        const ballastConverted = convertOutputValue(ballastThicknessWithSub.unstabilizedValue, ballastThicknessWithSub.unstabilizedUnit || 'm');
        const subballastConverted = convertOutputValue(subballastThickness.unstabilizedValue, subballastThickness.unstabilizedUnit || 'm');
        const deformationConverted = convertOutputValue(deformationWithSub.unstabilizedValue, deformationWithSub.unstabilizedUnit || 'mm');
        const rutDepthConverted = convertOutputValue(rutDepth.unstabilizedValue, rutDepth.unstabilizedUnit || 'mm');

        return (
            <View style={{ width: '48%', marginRight: '2%' }}>
                <PdfTable title="UNSTABILISED - with subballast" t={t}>
                    <PdfRow
                        label={ballastThicknessWithSub.description || 'N/A'}
                        symbol={symbols.sym_hbu}
                        value={ballastConverted.value}
                        unit={ballastConverted.unit}
                    />
                    <PdfRow
                        label={subballastThickness.description || 'N/A'}
                        symbol={symbols.sym_hsbu}
                        value={subballastConverted.value}
                        unit={subballastConverted.unit}
                    />
                    <PdfRow
                        label={deformationWithSub.description || 'N/A'}
                        symbol={symbols.sym_dvu}
                        value={deformationConverted.value}
                        unit={deformationConverted.unit}
                    />
                    <PdfRow
                        label={rutDepth.description || 'N/A'}
                        symbol={symbols.sym_ru}
                        value={rutDepthConverted.value}
                        unit={rutDepthConverted.unit}
                        colSpan={1}
                    />
                </PdfTable>
            </View>
        );
    };

    // 4. Stabilised with subballast
    const renderStabilisedWithSubballast = () => {
        if (!backendResponseData?.designSummary || backendResponseData.designSummary.length < 6) return null;

        const ballastThicknessWithSub = backendResponseData.designSummary[2];
        const subballastThickness = backendResponseData.designSummary[3];
        const deformationWithSub = backendResponseData.designSummary[4];
        const rutDepth = backendResponseData.designSummary[5];

        // Convert output values to Imperial if needed
        const ballastConverted = convertOutputValue(ballastThicknessWithSub.stabilizedValue, ballastThicknessWithSub.stabilizedUnit || 'm');
        const subballastConverted = convertOutputValue(subballastThickness.stabilizedValue, subballastThickness.stabilizedUnit || 'm');
        const deformationConverted = convertOutputValue(deformationWithSub.stabilizedValue, deformationWithSub.stabilizedUnit || 'mm');
        const rutDepthConverted = convertOutputValue(rutDepth.stabilizedValue, rutDepth.stabilizedUnit || 'mm');

        return (
            <View style={{ width: '48%', marginRight: '2%' }}>
                <PdfTable title="STABILISED - with subballast" t={t}>
                    <PdfRow
                        label={ballastThicknessWithSub.description || 'N/A'}
                        symbol={symbols.sym_hbs}
                        value={ballastConverted.value}
                        unit={ballastConverted.unit}
                    />
                    <PdfRow
                        label={subballastThickness.description || 'N/A'}
                        symbol={symbols.sym_hsbs}
                        value={subballastConverted.value}
                        unit={subballastConverted.unit}
                    />
                    <PdfRow
                        label={deformationWithSub.description || 'N/A'}
                        symbol={symbols.sym_dvs}
                        value={deformationConverted.value}
                        unit={deformationConverted.unit}
                    />
                    <PdfRow
                        label={rutDepth.description || 'N/A'}
                        symbol={symbols.sym_rs}
                        value={rutDepthConverted.value}
                        unit={rutDepthConverted.unit}
                        colSpan={1}
                    />
                </PdfTable>
            </View>
        );
    };

    // 5. Cargo train
    // Funzione di mapping per i simboli cargo (JSX) - locale alla tabella
    function getCargoSymbol(symbol: string | undefined): React.ReactElement | string {
        switch (symbol) {
            case 'Pc': return symbols.sym_pc;
            case 'Pdyn': return symbols.sym_pdyn;
            case 'σsb': return symbols.sym_sigma_sb;
            case 'N': return 'N'; // solo stringa, nessun JSX
            case 'Pp': return symbols.sym_pp;
            default: return symbol || 'N/A';
        }
    }
    // 5. Cargo train
    const renderCargoTrainTable = () => {
        if (!backendResponseData?.trainAnalysis) return null;

        const filteredData = backendResponseData.trainAnalysis.filter(row => {
            const symbol = row.cargoSymbol || '';
            return symbol === 'Pc' || symbol === 'Pdyn' || symbol === 'σsb' || symbol === 'N';
        });

        return (
            <View style={{ width: '48%', marginRight: '2%' }}>
                <PdfTable title="Cargo train" t={t}>
                    {filteredData.map((row, idx) => {
                        // Convert output values to Imperial if needed
                        // 2 decimals for the train-analysis values (wheel load / dynamic
                        // wheel load / uniform pressure); keep N (passage count) untouched.
                        const converted = typeof row.cargoValue === 'number'
                            ? convertOutputValue(row.cargoValue, row.cargoUnit || '', row.cargoSymbol === 'N' ? 3 : 2)
                            : { value: String(row.cargoValue ?? 'N/A'), unit: row.cargoUnit || 'N/A' };
                        return (
                            <PdfRow
                                key={idx}
                                label={row.description || 'N/A'}
                                symbol={getCargoSymbol(row.cargoSymbol)}
                                value={converted.value}
                                unit={converted.unit}
                                colSpan={row.cargoSymbol === 'N' ? 2 : 1}
                            />
                        );
                    })}
                </PdfTable>
            </View>
        );
    };

    // 6. Passenger train
    const renderPassengerTrainTable = () => {
        if (!backendResponseData?.trainAnalysis) return null;

        const filteredData = backendResponseData.trainAnalysis.filter(row => {
            const symbol = row.passengerSymbol || '';
            return symbol === 'Pp' || symbol === 'Pdyn' || symbol === 'σsb' || symbol === 'N';
        });

        return (
            <View style={{ width: '48%', marginRight: '2%' }}>
                <PdfTable title="Passenger train" t={t}>
                    {filteredData.map((row, idx) => {
                        // Convert output values to Imperial if needed
                        // 2 decimals for the train-analysis values; keep N (passage count) untouched.
                        const converted = typeof row.passengerValue === 'number'
                            ? convertOutputValue(row.passengerValue, row.passengerUnit || '', row.passengerSymbol === 'N' ? 3 : 2)
                            : { value: String(row.passengerValue ?? 'N/A'), unit: row.passengerUnit || 'N/A' };
                        return (
                            <PdfRow
                                key={idx}
                                label={row.description || 'N/A'}
                                symbol={getCargoSymbol(row.passengerSymbol)}
                                value={converted.value}
                                unit={converted.unit}
                                colSpan={row.passengerSymbol === 'N' ? 2 : 1}
                            />
                        );
                    })}
                </PdfTable>
            </View>
        );
    };

    return (
        <>
            {/* Title */}
            {customTitle && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title]}>{customTitle}</Text>
                    <View style={pdfSharedStyles.spacer1} />
                </>
            )}

            {!customTitle && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
            <Text style={[pdfSharedStyles.projectInfoProjectType]}>GROUNDMAC - Railway</Text>
                    <View style={pdfSharedStyles.spacer0} />
                    {/* <Text style={pdfSharedStyles.projectInfoSubtitle}>{t('railways_project_info_desc_pdf', 'Project Information')}</Text> */}
                    <Text>{parseHtmlToPdfText(t('railways_project_info_desc_pdf'), pdfSharedStyles.projectInfoSubtitle)}</Text>
                </>
            )}

            {/* Input Parameters Section - shown before images */}
            {showInputData && (
                <>
                    <View style={pdfSharedStyles.spacer1} />
                    <Text style={[pdfSharedStyles.title, { marginTop: 8, marginBottom: 0, textAlign: 'left' }]}>{t('input_data_summary')}</Text>

                    {/* Row 1: Railway Type & Load Data */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                        <View style={{ width: '48%' }}>
                            <PdfTable title="Railway Type" t={t}>
                                <PdfRow label={t('railway_type')} value={railwayTypeName} colSpan={3} />
                            </PdfTable>
                        </View>
                        <View style={{ width: '48%' }}>
                            <PdfTable title={t('load_data') || 'Load Data'} t={t}>
                                {/* User input fields for cargo_train or passenger_train */}
                                {railwayToDesign !== 'mixed_train' && (
                                    <>
                                        <PdfRow
                                            label={t('design_axle_load')}
                                            symbol="W"
                                            value={convertInputValue(projectInputs?.loadData?.designAxleLoad, 'designAxleLoad', 'kN', 'lbf')}
                                            unit={getUnit('kN', 'lbf')}
                                            condition={projectInputs?.loadData?.designAxleLoad !== undefined}
                                        />
                                        <PdfRow
                                            label={t('train_speed')}
                                            symbol="C"
                                            value={convertInputValue(projectInputs?.loadData?.trainSpeed, 'trainSpeed', 'km/h', 'mph')}
                                            unit={getUnit('km/h', 'mph')}
                                            condition={projectInputs?.loadData?.trainSpeed !== undefined}
                                        />
                                        <PdfRow
                                            label={t('wheel_diameter')}
                                            symbol={symbols.sym_dw}
                                            value={convertInputValue(projectInputs?.loadData?.wheelDiameter, 'wheelDiameter', 'm', 'in')}
                                            unit={getUnit('m', 'in')}
                                            condition={projectInputs?.loadData?.wheelDiameter !== undefined}
                                        />
                                    </>
                                )}
                                {/* Mixed train - Cargo fields */}
                                {railwayToDesign === 'mixed_train' && (
                                    <>
                                        <PdfRow label={t('cargo_train_parameters')} value="" colSpan={3} />
                                        <PdfRow
                                            label={t('design_axle_load')}
                                            symbol="W"
                                            value={convertInputValue(projectInputs?.loadData?.designAxleLoadCargo, 'designAxleLoad', 'kN', 'lbf')}
                                            unit={getUnit('kN', 'lbf')}
                                            condition={projectInputs?.loadData?.designAxleLoadCargo !== undefined}
                                        />
                                        <PdfRow
                                            label={t('train_speed')}
                                            symbol="C"
                                            value={convertInputValue(projectInputs?.loadData?.trainSpeedCargo, 'trainSpeed', 'km/h', 'mph')}
                                            unit={getUnit('km/h', 'mph')}
                                            condition={projectInputs?.loadData?.trainSpeedCargo !== undefined}
                                        />
                                        <PdfRow
                                            label={t('wheel_diameter')}
                                            symbol={symbols.sym_dw}
                                            value={convertInputValue(projectInputs?.loadData?.wheelDiameterCargo, 'wheelDiameter', 'm', 'in')}
                                            unit={getUnit('m', 'in')}
                                            condition={projectInputs?.loadData?.wheelDiameterCargo !== undefined}
                                        />
                                        <PdfRow label={t('passenger_train_parameters')} value="" colSpan={3} />
                                        <PdfRow
                                            label={t('design_axle_load')}
                                            symbol="W"
                                            value={convertInputValue(projectInputs?.loadData?.designAxleLoadPassenger, 'designAxleLoad', 'kN', 'lbf')}
                                            unit={getUnit('kN', 'lbf')}
                                            condition={projectInputs?.loadData?.designAxleLoadPassenger !== undefined}
                                        />
                                        <PdfRow
                                            label={t('train_speed')}
                                            symbol="C"
                                            value={convertInputValue(projectInputs?.loadData?.trainSpeedPassenger, 'trainSpeed', 'km/h', 'mph')}
                                            unit={getUnit('km/h', 'mph')}
                                            condition={projectInputs?.loadData?.trainSpeedPassenger !== undefined}
                                        />
                                        <PdfRow
                                            label={t('wheel_diameter')}
                                            symbol={symbols.sym_dw}
                                            value={convertInputValue(projectInputs?.loadData?.wheelDiameterPassenger, 'wheelDiameter', 'm', 'in')}
                                            unit={getUnit('m', 'in')}
                                            condition={projectInputs?.loadData?.wheelDiameterPassenger !== undefined}
                                        />
                                    </>
                                )}
                            </PdfTable>
                        </View>
                    </View>

                    {/* Row 2: Railway Geometry & Traffic Data */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                        <View style={{ width: '48%' }}>
                            <PdfTable title={t('railway_geometry') || 'Railway geometry'} t={t}>
                                <PdfRow label={t('rail_width')} symbol={symbols.sym_br} value={convertInputValue(projectInputs?.trackGeometry?.railWidth, 'railWidth', 'm', 'in')} unit={getUnit('m', 'in')} />
                                <PdfRow
                                    label={t('sleeper_thickness')}
                                    symbol={symbols.sym_ts}
                                    value={convertInputValue(projectInputs?.trackGeometry?.sleeperThickness, 'sleeperThickness', 'm', 'in')}
                                    unit={getUnit('m', 'in')}
                                />
                                <PdfRow
                                    label={t('sleeper_width')}
                                    symbol={symbols.sym_bs}
                                    value={convertInputValue(projectInputs?.trackGeometry?.sleeperWidth, 'sleeperWidth', 'm', 'in')}
                                    unit={getUnit('m', 'in')}
                                />
                                <PdfRow
                                    label={t('load_spreading_angle_in_sleepers')}
                                    symbol={symbols.sym_as}
                                    value={convertInputValue(projectInputs?.trackGeometry?.loadSpreadingAngle, 'loadSpreadingAngle', '°', '°')}
                                    unit="°"
                                />
                                <PdfRow
                                    label="Equivalent length of loaded area on top of ballast"
                                    symbol="L"
                                    value={convertInputValue(equivalentLength, 'equivalentLength', 'm', 'ft')}
                                    unit={getUnit('m', 'ft')}
                                />
                                <PdfRow label="Width of loaded area on top of ballast" symbol="bL" value={convertInputValue(widthLoadedArea, 'widthLoadedArea', 'm', 'ft')} unit={getUnit('m', 'ft')} />
                            </PdfTable>
                        </View>
                        <View style={{ width: '48%' }}>
                            <PdfTable title={t('traffic_data') || 'Traffic Data'} t={t}>
                                {/* User input fields for cargo_train or passenger_train */}
                                {railwayToDesign !== 'mixed_train' && (
                                    <>
                                        <PdfRow
                                            label={t('number_of_trains_per_day')}
                                            symbol="Nt"
                                            value={projectInputs?.trafficData?.numberOfTrainsPerDay?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfTrainsPerDay !== undefined}
                                        />
                                        <PdfRow
                                            label={t('number_of_cars_per_train')}
                                            symbol="Nw"
                                            value={projectInputs?.trafficData?.numberOfCarsPerTrain?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfCarsPerTrain !== undefined}
                                        />
                                        <PdfRow
                                            label={t('number_of_axis_per_car')}
                                            symbol={symbols.sym_Np}
                                            value={projectInputs?.trafficData?.numberOfAxisPerCar?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfAxisPerCar !== undefined}
                                        />
                                        <PdfRow
                                            label={t('design_life')}
                                            symbol="T"
                                            value={projectInputs?.trafficData?.designLife?.toString()}
                                            unit={t('years') || 'years'}
                                            condition={projectInputs?.trafficData?.designLife !== undefined}
                                        />
                                    </>
                                )}
                                {/* Mixed train - Cargo traffic fields */}
                                {railwayToDesign === 'mixed_train' && (
                                    <>
                                        <PdfRow label={t('cargo_train_traffic_data')} value="" colSpan={3} />
                                        <PdfRow
                                            label={t('number_of_trains_per_day')}
                                            symbol="Nt"
                                            value={projectInputs?.trafficData?.numberOfTrainsPerDayCargo?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfTrainsPerDayCargo !== undefined}
                                        />
                                        <PdfRow
                                            label={t('number_of_cars_per_train')}
                                            symbol="Nw"
                                            value={projectInputs?.trafficData?.numberOfCarsPerTrainCargo?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfCarsPerTrainCargo !== undefined}
                                        />
                                        <PdfRow
                                            label={t('number_of_axis_per_car')}
                                            symbol={symbols.sym_Np}
                                            value={projectInputs?.trafficData?.numberOfAxisPerCarCargo?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfAxisPerCarCargo !== undefined}
                                        />
                                        <PdfRow
                                            label={t('design_life')}
                                            symbol="T"
                                            value={projectInputs?.trafficData?.designLifeCargo?.toString()}
                                            unit={t('years') || 'years'}
                                            condition={projectInputs?.trafficData?.designLifeCargo !== undefined}
                                        />
                                        <PdfRow label={t('passenger_train_traffic_data')} value="" colSpan={3} />
                                        <PdfRow
                                            label={t('number_of_trains_per_day')}
                                            symbol="Nt"
                                            value={projectInputs?.trafficData?.numberOfTrainsPerDayPassenger?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfTrainsPerDayPassenger !== undefined}
                                        />
                                        <PdfRow
                                            label={t('number_of_cars_per_train')}
                                            symbol="Nw"
                                            value={projectInputs?.trafficData?.numberOfCarsPerTrainPassenger?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfCarsPerTrainPassenger !== undefined}
                                        />
                                        <PdfRow
                                            label={t('number_of_axis_per_car')}
                                            symbol={symbols.sym_Np}
                                            value={projectInputs?.trafficData?.numberOfAxisPerCarPassenger?.toString()}
                                            unit="-"
                                            condition={projectInputs?.trafficData?.numberOfAxisPerCarPassenger !== undefined}
                                        />
                                        <PdfRow
                                            label={t('design_life')}
                                            symbol="T"
                                            value={projectInputs?.trafficData?.designLifePassenger?.toString()}
                                            unit={t('years') || 'years'}
                                            condition={projectInputs?.trafficData?.designLifePassenger !== undefined}
                                        />
                                    </>
                                )}
                            </PdfTable>
                        </View>
                    </View>

                    {/* Page Break before Soil Properties */}
                    <View break />

                    {/* Row 3: Soil Properties & Geogrid Ballast */}
                    <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                        <View style={{ width: '48%' }}>
                            <PdfTable title={t('soil_properties') || 'Soil properties'} t={t}>
                                <PdfRow
                                    label="Thickness of soft subgrade"
                                    symbol={symbols.sym_hs}
                                    value={convertInputValue(projectInputs?.soilProperties?.thicknessOfSoftSubgrade, 'thicknessOfSoftSubgrade', 'm', 'ft')}
                                    unit={getUnit('m', 'ft')}
                                    condition={projectInputs?.soilProperties?.thicknessOfSoftSubgrade !== undefined}
                                />
                                <PdfRow
                                    label={t('cbr_of_ballast')}
                                    symbol={symbols.sym_cbrb}
                                    value={convertInputValue(projectInputs?.soilProperties?.cbrOfBallast, 'cbrOfBallast', '-', '-')}
                                    unit="-"
                                    condition={projectInputs?.soilProperties?.cbrOfBallast !== undefined}
                                />
                                <PdfRow
                                    label={t('cbr_of_subballast')}
                                    symbol={symbols.sym_cbrsb}
                                    value={convertInputValue(projectInputs?.soilProperties?.cbrOfSubballast, 'cbrOfSubballast', '-', '-')}
                                    unit="-"
                                    condition={projectInputs?.soilProperties?.cbrOfSubballast !== undefined}
                                />
                                <PdfRow
                                    label={t('cbr_of_subgrade')}
                                    symbol={symbols.sym_cbrsg}
                                    value={convertInputValue(projectInputs?.soilProperties?.cbrOfSubgrade, 'cbrOfSubgrade', '-', '-')}
                                    unit="-"
                                    condition={projectInputs?.soilProperties?.cbrOfSubgrade !== undefined}
                                />
                                <PdfRow
                                    label={t('poisson_ratio_of_ballast')}
                                    symbol={symbols.sym_ub}
                                    value={convertInputValue(projectInputs?.poissonRatios?.poissonRatioOfBallast, 'poissonRatioOfBallast', '-', '-')}
                                    unit="-"
                                    condition={projectInputs?.poissonRatios?.poissonRatioOfBallast !== undefined}
                                />
                                <PdfRow
                                    label={t('poisson_ratio_of_subballast')}
                                    symbol={symbols.sym_usb}
                                    value={convertInputValue(projectInputs?.poissonRatios?.poissonRatioOfSubballast, 'poissonRatioOfSubballast', '-', '-')}
                                    unit="-"
                                    condition={projectInputs?.poissonRatios?.poissonRatioOfSubballast !== undefined}
                                />
                                <PdfRow
                                    label={t('poisson_ratio_of_subgrade')}
                                    symbol={symbols.sym_usg}
                                    value={convertInputValue(projectInputs?.poissonRatios?.poissonRatioOfSubgrade, 'poissonRatioOfSubgrade', '-', '-')}
                                    unit="-"
                                    condition={projectInputs?.poissonRatios?.poissonRatioOfSubgrade !== undefined}
                                />
                                <PdfRow
                                    label={t('ballast_type')}
                                    symbol="-"
                                    value={getBallastLabel(projectInputs?.materialTypes?.ballastType ?? '')}
                                    unit="-"
                                    condition={!!projectInputs?.materialTypes?.ballastType}
                                />
                                <PdfRow
                                    label="Subgrade soil type"
                                    symbol="-"
                                    value={getSubgradeSoilTypeLabel(projectInputs?.materialTypes?.subgradeSoilType ?? '')}
                                    unit="-"
                                    condition={!!projectInputs?.materialTypes?.subgradeSoilType}
                                />
                            </PdfTable>
                        </View>
                        <View style={{ width: '48%' }}>
                            {selectedGeogrid && selectedGeogrid.trim() !== '' && selectedGeogrid !== 'none_selected' && (
                                <PdfTable title="Geogrid Ballast" t={t}>
                                    <PdfRow label={selectedGeogrid} colSpan={3} />
                                    <PdfRow
                                        label={t('reinforcement.tensileStrength2Percent') || 'Tensile strength of geogrid at 2% strain'}
                                        symbol="T₂%"
                                        value={tensileStrengthAt2Percent != null && Number.isFinite(tensileStrengthAt2Percent)
                                            ? (unitSystem === unitEnum.Imperial
                                                ? (tensileStrengthAt2Percent * 68.521959).toFixed(2)
                                                : tensileStrengthAt2Percent.toFixed(2))
                                            : 'N/A'}
                                        unit={getUnit('kN/m', 'lbf/ft')}
                                    />
                                    <PdfRow label={t('reinforcement.setBallastThickness') || 'Set Ballast thickness'} symbol={symbols.sym_hb} value={setBallastThicknessData.value} unit={setBallastThicknessData.unit} />
                                </PdfTable>
                            )}
                        </View>
                    </View>
                </>
            )}

            {/* Results Section with Track Images */}
            {showResults && (
                <>
                    {/* <View style={pdfSharedStyles.spacer1} /> */}
                    <Text style={[pdfSharedStyles.title, pdfSharedStyles.resultTitle]}>{t('results') || 'Results'}</Text>
                    

                    {/* Railway Track Images on new page */}
                    {(railwayImages?.tireImageUnreinforced || railwayImages?.tireImageReinforced) && (
                        <View style={pdfSharedStyles.graphicImagesContainer}>
                            <View style={{ alignItems: 'center', width: '50%' }}>
                                {railwayImages.tireImageUnreinforced ? (
                                    <Image key={railwayImages.tireImageUnreinforced.slice(0, 32)} style={[pdfSharedStyles.graphicImage, { width: '90%' }]} src={railwayImages.tireImageUnreinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Unreinforced track image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('unstabilised') || 'Unstabilised'}</Text>
                            </View>
                            <View style={{ alignItems: 'center', width: '50%' }}>
                                {railwayImages.tireImageReinforced ? (
                                    <Image key={railwayImages.tireImageReinforced.slice(0, 32)} style={[pdfSharedStyles.graphicImage, { width: '90%' }]} src={railwayImages.tireImageReinforced} />
                                ) : (
                                    <Text style={pdfSharedStyles.graphicDescription}>Reinforced track image not available</Text>
                                )}
                                <Text style={pdfSharedStyles.graphicDescription}>{t('stabilised') || 'Stabilised'}</Text>
                            </View>
                        </View>
                    )}

                    {/* Dynamic Result Tables - 6 tables below canvas */}
                    {backendResponseData && (
                        <>
                            <View style={pdfSharedStyles.spacer2} />

                            {/* BALLAST DESIGN TABLES (2x2 grid based on hasSubballast) */}
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                                {hasSubballast ? (
                                    <>
                                        {/* WITH SUBBALLAST: Show "Not stabilised with subballast" + "Stabilised with subballast" */}
                                        {renderNotStabilisedWithSubballast()}
                                        {renderStabilisedWithSubballast()}
                                    </>
                                ) : (
                                    <>
                                        {/* WITHOUT SUBBALLAST: Show "Not stabilised without subballast" + "Stabilised without subballast" */}
                                        {renderUnstabilisedWithoutSubballast()}
                                        {renderStabilisedWithoutSubballast()}
                                    </>
                                )}
                            </View>

                            {/* TRAIN ANALYSIS TABLES (1x2 grid based on railwayToDesign) */}
                            <View break />
                            <View style={pdfSharedStyles.spacer1} />
                            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 10 }}>
                                {railwayToDesign === 'cargo_train' && (
                                    <>
                                        {renderCargoTrainTable()}
                                        <View style={{ width: '48%' }} />
                                    </>
                                )}
                                {railwayToDesign === 'passenger_train' && (
                                    <>
                                        {renderPassengerTrainTable()}
                                        <View style={{ width: '48%' }} />
                                    </>
                                )}
                                {railwayToDesign === 'mixed_train' && (
                                    <>
                                        {renderCargoTrainTable()}
                                        {renderPassengerTrainTable()}
                                    </>
                                )}
                            </View>
                        </>
                    )}
                </>
            )}
        </>
    );
};

interface PdfRowProps {
    label: string;
    symbol?: string | React.ReactElement;
    value?: string;
    unit?: string;
    colSpan?: number;
    condition?: boolean;
    isEven?: boolean;
}

const PdfRow: React.FC<PdfRowProps> = ({ label, symbol = '', value = '', unit = '', colSpan = 1, condition = true, isEven = false }: PdfRowProps) => {
    if (condition === false) return null;

    // PM UX 2026-04-19: auto-merge Value+Unit when unit is absent (dash/empty/undefined)
    const effectiveColSpan = colSpan === 1 && typeof unit === 'string' && isEmptyUnit(unit) ? 2 : colSpan;

    const rowStyle = [pdfSharedStyles.tableRow, isEven ? pdfSharedStyles.evenRow : pdfSharedStyles.oddRow];

    let valueWidth = '20%';
    let showSymbol = true;
    let showUnit = true;

    if (effectiveColSpan === 3) {
        valueWidth = '65%'; // 25 (Symbol) + 20 (Value) + 20 (Unit)
        showSymbol = false;
        showUnit = false;
    } else if (effectiveColSpan === 2) {
        valueWidth = '40%'; // 20 (Value) + 20 (Unit)
        showUnit = false;
    }

    return (
        <View style={rowStyle}>
            <View style={[pdfSharedStyles.tableCol, pdfSharedStyles.dimFirstCol]}>
                <Text style={[pdfSharedStyles.tableCell, pdfSharedStyles.leftCell]}>{label}</Text>
            </View>

            {showSymbol && (
                <View style={pdfSharedStyles.tableColSymbol}>
                    <Text style={pdfSharedStyles.tableCell}>{symbol}</Text>
                </View>
            )}

            <View style={[pdfSharedStyles.tableColValue, { width: valueWidth }]}>
                <Text style={pdfSharedStyles.tableCell}>{value}</Text>
            </View>

            {showUnit && (
                <View style={pdfSharedStyles.tableColUnit}>
                    <Text style={pdfSharedStyles.tableCell}>{unit}</Text>
                </View>
            )}
        </View>
    );
};

interface PdfTableProps {
    title: string;
    children: React.ReactNode;
    t: TFunction;
}

const PdfTable: React.FC<PdfTableProps> = ({ title, children, t }: PdfTableProps) => {
    const validChildren = React.Children.toArray(children).filter((child: React.ReactNode): child is React.ReactElement => React.isValidElement(child));
    if (validChildren.length === 0) return null;

    return (
        <View style={[pdfSharedStyles.section, { width: '100%' }]}> 
            <View style={pdfSharedStyles.spacer1} />
            <Text style={pdfSharedStyles.tableTitle}>{title}</Text>
            <View style={pdfSharedStyles.spacer0} />
            <View style={pdfSharedStyles.table}>
                <View style={pdfSharedStyles.tableRow}>
                    <View style={[pdfSharedStyles.tableColHeader, pdfSharedStyles.dimFirstCol]}>
                        <Text style={[pdfSharedStyles.tableCellHeader, pdfSharedStyles.leftCell]}>{t('output_description')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderSymbol}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_symbol')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderValue}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_value')}</Text>
                    </View>
                    <View style={pdfSharedStyles.tableColHeaderUnit}>
                        <Text style={pdfSharedStyles.tableCellHeader}>{t('output_unit')}</Text>
                    </View>
                </View>
                {validChildren.map((child: React.ReactElement, idx: number) => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child as React.ReactElement<PdfRowProps>, { isEven: idx % 2 === 0 });
                    }
                    return child;
                })}
            </View>
        </View>
    );
}
